<template>
  <div class="top-bar-actions" data-testid="top-bar-actions">
    <!-- Favorite -->
    <button
      v-if="!mobileOverflow || !isMobile"
      data-testid="viewer-topbar-favorite"
      class="top-bar-actions__icon-btn"
      :title="favorite ? 'Remove from Favorites' : 'Add to Favorites'"
      :aria-label="favorite ? 'Remove from Favorites' : 'Add to Favorites'"
      @click="onFavoriteClick"
    >
      <Icon
        :name="favoriteIcon"
        size="base"
        :class="favorite ? 'text-copernicus-yellow' : ''"
        @mouseover="favoriteHovered = true"
        @mouseleave="favoriteHovered = false"
      />
    </button>

    <!-- Add to Collection -->
    <button
      v-if="!mobileOverflow || !isMobile"
      data-testid="viewer-topbar-collection"
      class="top-bar-actions__icon-btn"
      title="Add to Collection"
      aria-label="Add to Collection"
      @click="addToCollectionAction"
    >
      <Icon :name="collectionIcon" size="base" />
    </button>

    <!-- Download -->
    <button
      data-testid="viewer-topbar-download"
      class="top-bar-actions__icon-btn"
      :title="isEditProcessing ? 'Processing' : 'Download'"
      :aria-label="isEditProcessing ? 'Processing' : 'Download'"
      :disabled="isEditProcessing"
      @click="onDownloadClick"
    >
      <Icon name="general.download" size="base" />
    </button>

    <!-- Copy Link moves into the split Share control below. -->

    <!-- More (three dots) -->
    <ContextMenuWrapper placement="bottom-end" test-id="viewer-topbar-more" class="top-bar-actions__more-menu">
      <button data-testid="viewer-topbar-more-trigger" class="top-bar-actions__icon-btn" aria-label="More actions">
        <Icon name="general.meatballMoreDots" size="base" />
      </button>
      <template #menu>
        <template v-for="(item, index) in moreMenuItems" :key="item.name || `sep-${index}`">
          <MenuSeparator v-if="item.isSeparator" />
          <MenuToggle v-else-if="item.isToggle" :title="item.title" :type="item.type" v-model="item.value" @click.prevent="runAction(item)">
            {{ item.title }}
          </MenuToggle>
          <MenuLink
            v-else
            :icon="item.icon"
            :title="item.title"
            :type="item.type"
            :href="item.href"
            :disabled="item.disabled"
            :loading="item.loading"
            @click="runAction(item)"
          >
            {{ item.title }}
          </MenuLink>
        </template>
      </template>
    </ContextMenuWrapper>

    <div class="top-bar-actions__share-split" data-testid="viewer-topbar-share-split">
      <button class="top-bar-actions__share-primary" data-testid="viewer-topbar-share" @click.prevent="openShareModalAction">
        <Icon :name="getSharingIcon()" size="base" />
        <span>Share</span>
      </button>
      <button
        class="top-bar-actions__share-copy"
        data-testid="viewer-topbar-copy"
        title="Copy Link"
        aria-label="Copy Link"
        :disabled="!shareLink"
        @click="copyAction('viewer_toolbar')"
      >
        <Icon name="general.copy" size="base" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { Icon } from '@/storybook';
import { ContextMenuWrapper, MenuSeparator, MenuLink, MenuToggle } from '@/storybook/menu';
import { useMobileDetection } from '@/composables/useMobileDetection';
import type { ToolbarMenuItem } from './ToolbarMenuItem';

const props = withDefaults(
  defineProps<{
    mobileMenuItems?: ToolbarMenuItem[];
    mobileOverflow?: boolean;
  }>(),
  {
    mobileMenuItems: () => [],
    mobileOverflow: false,
  }
);

import { useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';
import { useCollectionsStore } from '@/store/useCollectionsStore';
import { useItemActions } from '@/modules/viewer/composables/useItemActions';
import { useItemActionsToolbar } from '@/modules/viewer/composables/useItemActionsToolbar';
import { useItemShare } from '@/modules/viewer/composables/useItemShare';
import { TrackEvents, useViewerTrack } from '@/modules/viewer/composables/useViewerTrack';

const { isMobile } = useMobileDetection();

const viewerItemStore = useViewerItemStore();
const { id, favorite, isEditProcessing, shareLink } = storeToRefs(viewerItemStore);
const { list } = storeToRefs(useCollectionsStore());
const { trackItemOwner } = useViewerTrack();

const { downloadAction, addToCollectionAction, openShareModalAction, copyAction } = useItemActions();
const { itemActionsMenu } = useItemActionsToolbar();
const { getSharingIcon } = useItemShare();

const favoriteHovered = ref(false);
const isSavingFavorite = ref(false);

const favoriteIcon = computed(() => {
  if (!favorite.value) {
    return favoriteHovered.value ? 'general.favoritesSolid' : 'general.favorites';
  }
  return 'general.favoritesSolid';
});

const hasCollection = computed(() => {
  if (id.value === undefined) {
    return false;
  }
  return list.value ? list.value.filter(col => col.items.includes(id.value || '')).length > 0 : false;
});

const collectionIcon = computed(() => (hasCollection.value ? 'general.folderFull' : 'general.folder'));

const moreMenuItems = computed(() => {
  if (!itemActionsMenu.value) {
    return [];
  }
  const mobileMenuItems: ToolbarMenuItem[] =
    props.mobileOverflow && isMobile.value
      ? [
          ...props.mobileMenuItems,
          {
            title: favorite.value ? 'Remove from Favorites' : 'Add to Favorites',
            name: favorite.value ? 'favorites-remove' : 'favorites-add',
            icon: favorite.value ? 'general.favoritesSolid' : 'general.favorites',
            type: 'secondary',
            action: onFavoriteClick,
          },
          {
            title: 'Add to Collection',
            name: 'collection',
            icon: collectionIcon.value,
            type: 'secondary',
            action: addToCollectionAction,
          },
        ]
      : [];
  const filtered = itemActionsMenu.value.filter(
    (item: ToolbarMenuItem) => !['favorites-add', 'favorites-remove', 'collection', 'download', 'copy-link'].includes(item.name || '')
  );

  return [...mobileMenuItems, ...filtered].filter((item: ToolbarMenuItem, index: number, arr: ToolbarMenuItem[]) => {
    if (!item.isSeparator) {
      return true;
    }
    const isFirst = index === 0;
    const isLast = index === arr.length - 1;
    const prevIsSeparator = index > 0 && arr[index - 1].isSeparator;
    return !isFirst && !isLast && !prevIsSeparator;
  });
});

const onFavoriteClick = async () => {
  if (isSavingFavorite.value) {
    return;
  }
  trackItemOwner(TrackEvents.ITEM_ADD_TO_FAVORITES);
  isSavingFavorite.value = true;
  try {
    await viewerItemStore.toggleFavorite(id.value);
  } finally {
    isSavingFavorite.value = false;
  }
};

const onDownloadClick = async () => {
  if (isEditProcessing.value) {
    return;
  }
  await downloadAction();
};

const runAction = (item: ToolbarMenuItem) => {
  if (typeof item?.action === 'function') {
    return item.action();
  }
};
</script>

<style scoped>
.top-bar-actions {
  @apply flex items-center gap-1 sm:gap-2;
}

.top-bar-actions__icon-btn {
  @apply p-2 rounded-md text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed;
  min-width: 40px;
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.top-bar-actions__share-split {
  @apply flex h-9 overflow-hidden rounded-md bg-blue-500 text-white;
}

.top-bar-actions__share-primary,
.top-bar-actions__share-copy {
  @apply flex items-center justify-center gap-2 px-3 text-sm font-bold transition-colors hover:bg-blue-600 active:bg-blue-700 disabled:opacity-50;
}

.top-bar-actions__share-copy {
  @apply w-9 border-l border-white/30 px-0;
}

.top-bar-actions__more-menu :deep(.context-menu) {
  max-height: calc(100dvh - 6rem);
  overflow-y: auto;
}
</style>
